import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * A JSON object socket. Null bags are not valid here — the handler replaces
 * them with `{}` before this schema runs.
 */
const jsonObjectSchema = z.record(z.string(), z.unknown());

/**
 * One catalog variant. `productId` is null when the id was not in this
 * organization's catalog and the step is set to return empty. The requested
 * id is still present so a list stays aligned with its inputs.
 */
export const productVariantFieldsSchema = z.object({
  productVariantId: z.uuid().describe("The requested variant id"),
  productId: z
    .uuid()
    .nullable()
    .describe(
      "Parent ProductV2 id. Null when the variant was not found and the step returns empty.",
    ),
  sku: z.string().nullable().describe("Variant SKU, when present"),
  active: z
    .boolean()
    .nullable()
    .describe(
      "Whether the variant is active. Null when the catalog has no flag.",
    ),
  attributes: jsonObjectSchema.describe(
    "Variant attribute bag. Null in the catalog is {}.",
  ),
  originId: z.string().nullable().describe("Id in the origin system"),
  originSystem: z
    .string()
    .nullable()
    .describe("Origin system that produced the variant"),
  originEntity: z
    .string()
    .nullable()
    .describe("Origin entity name, when present"),
  originMetadata: jsonObjectSchema.describe(
    "Origin metadata bag (for example Famous NAMEINVC). Null in the catalog is {}.",
  ),
  originAttributes: jsonObjectSchema.describe(
    "Origin attribute bag. Null in the catalog is {}.",
  ),
});

export const getProductVariantSingleInputSchema = z.object({
  productVariantId: z
    .uuid()
    .describe(
      "Catalog product variant id. A list of ids splits into one lookup per id unless list mode is on.",
    ),
});

export const getProductVariantListInputSchema = z.object({
  productVariantIds: z
    .array(z.uuid())
    .describe(
      "Catalog product variant ids. The step returns one variant per id, in this order, without splitting the run.",
    ),
});

export const getProductVariantListOutputSchema = z.object({
  variants: z
    .array(productVariantFieldsSchema)
    .describe(
      "Catalog variants in the same order as the input ids. A missing id is an empty variant when return empty is on.",
    ),
});

export const getProductVariantConfigSchema = z.object({
  listMode: z
    .boolean()
    .prefault(false)
    .describe(
      "Look up every id in one step and return the variants together. When this is off, one id is looked up per run, and a list of ids splits into a branch per id.",
    ),
  returnEmptyIfNotFound: z
    .boolean()
    .prefault(false)
    .describe(
      "When an id is not in this organization's catalog, return an empty variant and continue. When this is off, a missing id fails the run.",
    ),
});

/**
 * Get Product Variant
 *
 * Loads `im_product_variants` rows by id and exposes catalog fields,
 * including `originMetadata` (for example Famous `NAMEINVC`). Match Supplier
 * Document Lines already puts `productVariantId` on each matched line.
 *
 * The default input is a single uuid, so an array of ids fans out to one
 * lookup per line. List mode takes the array on an `array<uuid>` socket and
 * returns the variants together.
 */
export const getProductVariantStep = defineStepConfig({
  type: "get_product_variant",
  name: "Get Product Variant",
  description:
    "Load catalog product variants by id for the run's organization, including originMetadata (for example Famous NAMEINVC). List mode takes ids in and returns the variants together. Otherwise one id is looked up per run, and a list of ids splits into a branch per id.",
  category: "Data Access",
  libraryModule: "ItemMaster",

  inputSchema: z.union([
    getProductVariantSingleInputSchema,
    getProductVariantListInputSchema,
  ]),

  outputSchema: z.union([
    productVariantFieldsSchema,
    getProductVariantListOutputSchema,
  ]),

  configSchema: getProductVariantConfigSchema,

  emits: [] as const,
});
